import { useCallback, useEffect, useRef, useState } from "react";

// Hands-free voice command via the browser's built-in Speech Recognition.
// Great on Chrome/Android; limited/unreliable on iOS Safari (we expose `supported`).
export function useVoiceCapture(onMatch, { keywords = ["capture"] } = {}) {
  const SR =
    typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);
  const supported = !!SR;

  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const activeRef = useRef(false);
  const lastFireRef = useRef(0);
  const matchRef = useRef(onMatch);
  matchRef.current = onMatch;

  const stop = useCallback(() => {
    activeRef.current = false;
    setListening(false);
    try {
      recRef.current && recRef.current.stop();
    } catch {}
  }, []);

  const start = useCallback(() => {
    if (!supported) return;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";

    rec.onresult = (event) => {
      let text = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        text += event.results[i][0].transcript;
      }
      text = text.toLowerCase();
      const hit = keywords.some((k) => text.includes(k));
      const now = Date.now();
      if (hit && now - lastFireRef.current > 1500) {
        lastFireRef.current = now;
        matchRef.current && matchRef.current();
      }
    };

    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        activeRef.current = false;
        setListening(false);
      }
    };

    // Browsers end recognition after silence — restart while still active.
    rec.onend = () => {
      if (activeRef.current) {
        try {
          rec.start();
        } catch {}
      } else {
        setListening(false);
      }
    };

    recRef.current = rec;
    activeRef.current = true;
    try {
      rec.start();
      setListening(true);
    } catch {
      activeRef.current = false;
      setListening(false);
    }
  }, [SR, supported, keywords]);

  useEffect(() => () => stop(), [stop]);

  return { supported, listening, start, stop };
}
